/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
import currency from "currency.js";
import {
  InstrumentTypeResponse,
  MarginTypeResponse,
} from "../../codegen-api";
import { COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { ITableColumn } from "../../interfaces/Table/TableColumn";
import {
  formatKnownPrice,
  formatKnownValue,
  formatRoiPercent,
  signedTone,
} from "../../utils/unknownNumbers";
import {
  formatCurrencyWithSubscript,
  formatSizePrecision,
} from "../../utils/format";
import { getMarginTypeLabel, normalizeMarginType } from "../../utils/margin";
import { apiNum } from "../../utils/math";
import { Align, SizeFillCell } from "../shared/Table/style";
import ActionButtonsCell from "./ActionButtonsCell";
import {
  PnlHeader,
  buildGreekCols,
  buildIvCols,
  buildMarketCol,
  buildPerpsCols,
} from "./columnGroups";
import { ACTION_BUTTON_ID, numericSort } from "./helpers";
import { IModifiedPosition } from "./model";
import { BuildColumnsArgs } from "./types";

export function buildColumns(
  args: BuildColumnsArgs
): ITableColumn<IModifiedPosition>[] {
  const {
    t,
    tooltip,
    accountData,
    modifiedPositions,
    selectedMarketType,
    inLiquidation,
    onClosePositionWithId,
    setViewDetailsForPositionId,
    setShareModalPositionId,
    setAdjustingMarginPositionId,
    onEditTpsl,
  } = args;

  const ivCols = buildIvCols(t, tooltip);
  const greekCols = buildGreekCols(t, tooltip);
  const hasPerpsCol = buildPerpsCols(t, tooltip, onEditTpsl);

  const cols: ITableColumn<IModifiedPosition>[] = [
    buildMarketCol(t, tooltip, accountData),
    {
      title: t("contracts"),
      accessor: "amount",
      align: "right",
      tooltip: tooltip("positionSize"),
      Cell: ({ value, row }) => {
        if (row.original.nestedOptionsData) {
          return null;
        }
        return (
          <Align align="right">
            {formatSizePrecision(value, row.original.amountPrecision)}
          </Align>
        );
      },
    },
    {
      title: t("avg_entry_price"),
      accessor: "avg_entry_price",
      align: "right",
      tooltip: tooltip("avgEntryPrice"),
      Cell: ({ value, row }) => {
        if (row.original.nestedOptionsData) {
          return null;
        }
        return (
          <Align align="right">
            {currency(value, {
              precision: row.original.pricePrecision,
            }).format()}
          </Align>
        );
      },
    },
    {
      title: t("position_value"),
      accessor: "positionValue",
      align: "right",
      tooltip: tooltip("positionValue"),
      Cell: ({ value, row }) => {
        if (row.original.nestedOptionsData) {
          return null;
        }
        // Unknown mark, unknown value: "-", not "$0.00".
        return <Align align="right">{formatKnownValue(value)}</Align>;
      },
    },
    {
      title: t("mark_price"),
      accessor: "mark_price",
      align: "right",
      tooltip: tooltip("markPrice"),
      Cell: ({ value, row }) => {
        if (row.original.nestedOptionsData) {
          return null;
        }
        return (
          <Align align="right">
            {formatKnownPrice(value, row.original.pricePrecision)}
          </Align>
        );
      },
    },
    {
      title: t("unrealized_pnl"),
      accessor: "unrealized_pnl",
      align: "right",
      tooltip: tooltip("unrealizedPnl"),
      sortType: numericSort,
      Header: ({ column }: any) => (
        <PnlHeader
          title={t("unrealized_pnl")}
          explanation={tooltip("unrealizedPnl")}
          column={column}
        />
      ),
      Cell: ({ value, row }) => {
        if (row.original.nestedOptionsData) {
          return null;
        }

        const { roi } = row.original;
        // Unknown PnL / ROI are neutral "-": `Number(undefined) >= 0` is false,
        // which painted an unknown red and printed "NaN%".
        const color = signedTone(value, {
          up: COLORS.up.one,
          down: COLORS.down.one,
          unknown: TEXT_COLORS.three,
        });
        const roiColor = signedTone(roi, {
          up: COLORS.up.two,
          down: COLORS.down.two,
          unknown: TEXT_COLORS.three,
        });
        return (
          <Align align="right">
            <SizeFillCell>
              <span style={{ color }}>
                {formatCurrencyWithSubscript(value, {
                  showPositiveSign: true,
                })}
              </span>
              {roi !== undefined && roi !== null ? (
                <span style={{ color: roiColor }}>{formatRoiPercent(roi)}</span>
              ) : null}
            </SizeFillCell>
          </Align>
        );
      },
    },
    /* R-PNL (Realized PnL) column hidden per request — uncomment to restore.
    {
      title: t("realized_pnl"),
      accessor: "realized_pnl",
      align: "right",
      tooltip: tooltip("realizedPnl"),
      sortType: numericSort,
      Header: ({ column }: any) => (
        <PnlHeader
          title={t("realized_pnl")}
          explanation={tooltip("realizedPnl")}
          column={column}
        />
      ),
      Cell: ({ row }: any) => {
        if (row.original.nestedOptionsData) {
          return null;
        }
        const raw = row.original.realized_pnl;
        const realized = raw !== undefined && raw !== null ? Number(raw) : NaN;
        const hasValue = Number.isFinite(realized);
        // Same canonical formula as U.PNL — see src/utils/positionRoi.ts.
        const marginBasis = pickMarginBasis(row.original);
        const realizedPct =
          hasValue && marginBasis > 0 ? (realized / marginBasis) * 100 : NaN;
        const color = !hasValue
          ? TEXT_COLORS.three
          : realized >= 0
            ? COLORS.up.one
            : COLORS.down.one;
        const pctColor = !Number.isFinite(realizedPct)
          ? TEXT_COLORS.three
          : realizedPct >= 0
            ? COLORS.up.two
            : COLORS.down.two;
        return (
          <Align align="right">
            <SizeFillCell>
              <span style={{ color }}>
                {hasValue
                  ? formatCurrencyWithSubscript(realized, {
                    showPositiveSign: true,
                  })
                  : "-"}
              </span>
              <span style={{ color: pctColor }}>
                {Number.isFinite(realizedPct)
                  ? `${realizedPct >= 0 ? "+" : ""}${realizedPct.toFixed(2)}%`
                  : "-"}
              </span>
            </SizeFillCell>
          </Align>
        );
      },
    },
    */
    ...(modifiedPositions.some(
      (pos) => pos.instrument_type === InstrumentTypeResponse.Perpetual
    )
      ? hasPerpsCol
      : []),
    ...(selectedMarketType === InstrumentTypeResponse.Option ? ivCols : []),
    {
      // Margin amount + type combined, e.g. "$33.56 (Cross)".
      title: t("margin", { defaultValue: "Margin" }),
      align: "right",
      accessor: "maintenance_margin",
      tooltip: tooltip("maintenanceMargin"),
      Cell: ({ value, row }) => {
        if (row.original.nestedOptionsData) {
          return null;
        }
        const marginType = normalizeMarginType(row.original.margin_type);
        const margin =
          marginType === MarginTypeResponse.Isolated
            ? row.original.isolated_margin
            : value;

        return (
          <Align align="right">
            <SizeFillCell>
              {/* Null (unpriced market) is "-"; `margin || 0` printed $0.00. */}
              <span>{formatCurrencyWithSubscript(apiNum(margin))}</span>
              <span>({getMarginTypeLabel(row.original.margin_type)})</span>
            </SizeFillCell>
          </Align>
        );
      },
    },
    ...(selectedMarketType === InstrumentTypeResponse.Option
      ? greekCols
      : []),
    {
      id: ACTION_BUTTON_ID,
      align: "right",
      Header: () => <div />,
      Cell: ({ row }: any) => (
        <ActionButtonsCell
          shareColor={signedTone(row.original.roi, {
            up: COLORS.up.one,
            down: COLORS.down.one,
            unknown: TEXT_COLORS.three,
          })}
          inLiquidation={inLiquidation}
          position={row.original}
          onClosePositionWithId={onClosePositionWithId}
          setViewDetailsForPositionId={setViewDetailsForPositionId}
          setShareModalPositionId={setShareModalPositionId}
          setAdjustingMarginPositionId={setAdjustingMarginPositionId}
        />
      ),
    },
    // Force columns to rerender when sortInfo changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ];
  return cols;
}
